import React, { useLayoutEffect, useRef } from 'react';
import { extend, ThreeElement, useThree } from '@react-three/fiber';
import type { EventHandlers } from '@react-three/fiber/dist/declarations/src/core/events';
import { ColorRepresentation, Line as ThreeLine, Vector3 } from 'three';

declare module '@react-three/fiber' {
  interface ThreeElements {
    threeLine: ThreeElement<typeof ThreeLine>;
  }
}

extend({ ThreeLine });

export type LinePoints = readonly [readonly [number, number, number], readonly [number, number, number]];

interface Props extends EventHandlers {
  readonly points: LinePoints;
  readonly color: ColorRepresentation;

  readonly visible?: boolean;
  readonly infinite?: boolean;

  readonly depthTest?: boolean;

  readonly dashed?: boolean;
}

export function Line({
  points,
  color,
  visible = true,
  infinite = false,
  depthTest = true,
  dashed = false,

  ...eventHandlers
}: Props) {
  const ref = useRef<ThreeLine | null>(null);
  const { invalidate } = useThree();

  useLayoutEffect(() => {
    if (!ref.current) return;

    const base = new Vector3(...points[0]);
    const end = new Vector3(...points[1]);

    if (infinite) {
      const direction = new Vector3().subVectors(end, base).normalize();

      base.addScaledVector(direction, -5000);
      end.addScaledVector(direction, 5000);
    }

    ref.current.geometry.setFromPoints([base, end]);
    ref.current.computeLineDistances();

    invalidate();
  }, [infinite, invalidate, points]);
  return (
    <threeLine ref={ref} visible={visible} {...eventHandlers}>
      <bufferGeometry />

      {dashed ? (
        <lineDashedMaterial color={color} depthTest={depthTest} />
      ) : (
        <lineBasicMaterial color={color} depthTest={depthTest} />
      )}
    </threeLine>
  );
}
